import { AxiosError, AxiosResponse } from "axios";
import {
  all,
  delay,
  put,
  select,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import { Urls } from "src/constants";
import { ProgramType } from "src/enums";
import { ProgramInfo, ProgramMessage, QualityConfig, Stream } from "src/models";
import { HttpService, PlayerService, SeoService } from "src/services";
import { ProgramViewAction } from "../actions";
import { ConfigInit, ProgramDetail, ReduxAction } from "../models";
import { ProgramViewTypes } from "../types";

export function* ProgramViewSaga() {
  yield takeLatest(ProgramViewTypes.REQUEST_PROGRAM_VIEW_INFO, requestInfo);
  yield takeLatest(ProgramViewTypes.REQUEST_PROGRAM_STREAM_DATA, requestStream);
  yield takeLatest(ProgramViewTypes.SET_CURRENT_STREAM, requestChangeStream);
  yield takeLatest(ProgramViewTypes.START_PLAYBACK_REPORTING, doReporting);
  yield takeLatest(ProgramViewTypes.DO_REPORT_PLAYBACK, reportPlayback);
  yield takeLatest(ProgramViewTypes.DO_REPORT_STATISTIC, reportStatistic);
  yield takeLatest(ProgramViewTypes.SET_PROGRAM_LOCKED, setProgramLocked);
  yield takeLeading(ProgramViewTypes.CHECK_FORBIDDEN_IP, checkIp);
  yield takeLatest(ProgramViewTypes.TOGGLE_ECONOMIC, toggleEconomic);
}

function* doReporting(action: ReduxAction) {
  yield all([reportPlayback(action), reportStatistic(action)]);
}

function* reportPlayback(action: ReduxAction) {
  try {
    const config: ConfigInit = yield select((state: any) => state.ConfigState);
    yield delay(config.config.appSetting.playReportDuration * 1000);

    const programInfo: ProgramDetail = yield select(
      (state: any) => state.ProgramViewState
    );
    const { streamId } = action.payload;

    if (!programInfo.reporting || programInfo.currentStream?.id !== streamId)
      return;

    const api = programInfo.player?._links.find(
      (x) => x.rel === "report-playback"
    );
    if (!api) return;

    yield HttpService.Fetch(Urls.packApi(null, api.href, api.method, false));

    yield put(ProgramViewAction.reportPlayback(action.payload));
  } catch (ex: any) {
    const error: AxiosError = ex;
    if (!error.response) return;

    if (error.response.status >= 500) {
      yield put(ProgramViewAction.reportPlayback(action.payload));
      return;
    }

    const data = error.response.data || {};
    yield PlayerService.setProgramAvailabilityByStatus(
      data,
      error.response.status
    );
  }
}

function* reportStatistic(action: ReduxAction) {
  try {
    const config: ConfigInit = yield select((state: any) => state.ConfigState);
    yield delay(config.config.appSetting.statReportDuration * 1000);

    const programInfo: ProgramDetail = yield select(
      (state: any) => state.ProgramViewState
    );
    const { streamId } = action.payload;

    if (!programInfo.reporting || programInfo.currentStream?.id !== streamId)
      return;
    const api = programInfo.player?._links.find((x) => x.rel === "stats");
    if (!api) return;

    yield HttpService.Fetch(Urls.packApi(null, api.href, api.method, false));

    yield put(ProgramViewAction.reportStatistic(action.payload));
  } catch (ex: any) {
    const error: AxiosError = ex;
    if (!error.response) return;

    if (error.response.status >= 500) {
      yield put(ProgramViewAction.reportStatistic(action.payload));
      return;
    }

    const data = error.response.data || {};
    yield PlayerService.setProgramAvailabilityByStatus(
      data,
      error.response.status
    );
  }
}

function* requestChangeStream(action: ReduxAction) {
  const programInfo: ProgramDetail = yield select(
    (state: any) => state.ProgramViewState
  );
  yield put(
    ProgramViewAction.requestProgramStreamData(
      action.payload,
      programInfo.info?.qualityConfig
    )
  );
}

function* toggleEconomic(action: ReduxAction) {
  const programInfo: ProgramDetail = yield select(
    (state: any) => state.ProgramViewState
  );

  const qc: any = {
    ...programInfo.info?.qualityConfig,
    hqEnabled: action.payload.economic,
  };

  yield put(
    ProgramViewAction.requestProgramStreamData(action.payload.stream, qc)
  );
}

function* requestInfo(action: ReduxAction) {
  try {
    const { id, programType } = action.payload;
    const api = Urls.program.getById(id);
    yield getPromotion(id);
    const response: AxiosResponse = yield HttpService.Fetch(api);
    const info: ProgramInfo = response.data;

    if (info.streams) {
      info.streams.forEach((stream) => {
        stream.lock = stream.subs.every((x) => x.lock);
      });
    }

    if (info.type !== (programType || ProgramType.Live)) {
      yield put(ProgramViewAction.setProgramInfo(undefined));
      yield PlayerService.setProgramAvailabilityByStatus({}, 404);
      return;
    }

    SeoService.setHeaderMetadata(info.title, info.cover);

    info.fetchTime = new Date().getTime();
    info.calculatedRemainingToStart = info.remainingToStart;
    yield put(ProgramViewAction.setProgramInfo(info));
    const programInfo: ProgramDetail = yield select(
      (state: any) => state.ProgramViewState
    );
    const isAvailable: boolean =
      yield PlayerService.setProgramAvailabilityByInfo();

    if (!isAvailable || !programInfo.currentStream) return;

    yield put(
      ProgramViewAction.requestProgramStreamData(
        programInfo.currentStream,
        programInfo.info?.qualityConfig
      )
    );
  } catch (ex: any) {
    const error: AxiosError = ex;

    if (
      error.response &&
      (error.response.status === 404 || error.response.status >= 500)
    ) {
      yield put(ProgramViewAction.setProgramInfo(undefined));
      yield PlayerService.setProgramAvailabilityByStatus({}, 404);
      return;
    }

    yield put(ProgramViewAction.hideLoading());
  }
}

function* getPromotion(id: number) {
  try {
    const api = Urls.promotions.getByProgramId(id);
    const response: AxiosResponse = yield HttpService.Fetch(api);
    if (response.data) {
      yield put(ProgramViewAction.setPromotion(response.data));
    }
  } catch {}
}

function* setProgramLocked() {
  yield PlayerService.setProgramLocked();
}

function* requestStream(action: ReduxAction) {
  try {
    const programInfo: ProgramDetail = yield select(
      (state: any) => state.ProgramViewState
    );

    const {
      stream,
      qualityConfig,
    }: { stream: Stream; qualityConfig?: QualityConfig } = action.payload;

    if (!qualityConfig) return;

    const url =
      //@ts-ignore
      stream[
        qualityConfig?.hqEnabled
          ? qualityConfig.hqQualityUrlAccess
          : qualityConfig.lowQualityUrlAccess
      ];

    const response: AxiosResponse = yield HttpService.Fetch(
      Urls.packApi(undefined, url, "GET", true)
    );

    yield put(
      ProgramViewAction.setProgramPlayerData(
        response.data,
        programInfo.info?.id
      )
    );
  } catch (ex: any) {
    const error: AxiosError = ex;
    if (!error.response) return;

    const data = error.response.data || {};
    yield PlayerService.setProgramAvailabilityByStatus(
      data,
      error.response.status
    );
  }
}

function* checkIp() {
  try {
    const message: ProgramMessage = yield select(
      (state) => state.ProgramViewState.message
    );
    const link = message.links?.find((x) => x.rel === "report");
    if (!link) {
      yield put(ProgramViewAction.IpChecked(false, ""));
      return;
    }
    const response: AxiosResponse = yield HttpService.Fetch(
      Urls.packApi(null, link.href, link.method, false)
    );
    yield put(ProgramViewAction.IpChecked(true, response?.data?.message || ""));
  } catch {
    yield put(ProgramViewAction.IpChecked(false, ""));
  }
}
